import { isValidWalletAddress, normalizeNumberString } from './validators'
import type { WhitelistEntry, ReferralEntry } from './types'

export type WhitelistCsvTarget = 'whitelisted' | 'private'

export async function parseWhitelistCsvFile(file: File): Promise<{
  entries: WhitelistEntry[]
  invalid: number
  duplicates: number
  error?: string
}> {
  try {
    const text = await file.text()
    const rawLines = text.split(/\r?\n/)
    const lines = rawLines.map((l) => l.trim()).filter((l) => l.length > 0)
    if (lines.length < 2) return { entries: [], invalid: 0, duplicates: 0 }
    const header = lines[0].split(',').map((h) => h.trim().toLowerCase())
    const hasWalletHeader = header[0] === 'wallet_address'
    const hasAllocationHeader =
      header[1] === 'allocation_in_wei' || header[1] === 'allocation'
    if (!hasWalletHeader || !hasAllocationHeader)
      return {
        entries: [],
        invalid: 0,
        duplicates: 0,
        error: 'Invalid CSV header. Use the provided template.',
      }
    const addrIdx = 0
    const allocIdx = 1
    const entries: WhitelistEntry[] = []
    const seen = new Set<string>()
    let invalid = 0
    let duplicates = 0
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',')
      const rawAddr = (addrIdx > -1 ? cols[addrIdx] : cols[0] || '').trim()
      const rawAlloc = (allocIdx > -1 ? cols[allocIdx] : cols[1] || '').trim()
      if (!isValidWalletAddress(rawAddr)) {
        invalid++
        continue
      }
      const lower = rawAddr.toLowerCase()
      if (seen.has(lower)) {
        duplicates++
        continue
      }
      seen.add(lower)
      const normalizedAlloc = normalizeNumberString(rawAlloc)
      const n = Number(normalizedAlloc)
      entries.push({
        walletAddress: rawAddr,
        allocationAmount:
          Number.isFinite(n) && normalizedAlloc !== '' ? normalizedAlloc : '',
      })
    }
    return { entries, invalid, duplicates }
  } catch {
    return { entries: [], invalid: 0, duplicates: 0 }
  }
}

export async function parseReferralCsvFile(file: File): Promise<{
  entries: ReferralEntry[]
  invalid: number
  duplicates: number
  error?: string
}> {
  const TEMPLATE_HEADER = [
    'promo tier',
    't2 wallet address',
    't3 wallet address',
    'discount %',
    'kickback %',
    'promo code',
    't2 share %',
    't3 share %',
    'name',
    'telegram contact',
  ]

  const cleanHeaderValue = (value: string): string =>
    value
      .replace(/^\ufeff/, '')
      .trim()
      .toLowerCase()

  const parseNumber = (value: string): number => {
    const normalized = normalizeNumberString(value)
    const num = Number(normalized)
    return Number.isFinite(num) ? num : 0
  }

  const findHeader = (lines: string[]) => {
    for (let i = 0; i < lines.length; i++) {
      const cells = lines[i].split(',').map((h) => cleanHeaderValue(h || ''))
      if (!cells.some((cell) => cell.length > 0)) continue
      const hasTemplate = TEMPLATE_HEADER.every((header) =>
        cells.includes(header),
      )
      if (hasTemplate) return { index: i, cells }
    }
    return null
  }

  try {
    const text = await file.text()
    const rawLines = text.split(/\r?\n/)
    const headerInfo = findHeader(rawLines)

    if (!headerInfo)
      return {
        entries: [],
        invalid: 0,
        duplicates: 0,
        error:
          'Invalid CSV header. Please use the latest Custom Referral Template.',
      }

    const dataLines = rawLines.slice(headerInfo.index + 1)
    const entries: ReferralEntry[] = []
    let invalid = 0
    let duplicates = 0

    const headerIndex = new Map<string, number>()
    headerInfo.cells.forEach((header, idx) => {
      if (!headerIndex.has(header)) headerIndex.set(header, idx)
    })

    const getValue = (cols: string[], key: string) => {
      const idx = headerIndex.get(key)
      if (idx === undefined) return ''
      return (cols[idx] || '').replace(/^"(.*)"$/, '$1').trim()
    }

    const seenContacts = new Set<string>()

    for (const rawLine of dataLines) {
      const line = rawLine.trim()
      if (!line) continue
      const cols = rawLine.split(',')
      if (cols.every((c) => !c.trim())) continue

      const promoTier = getValue(cols, 'promo tier')
      const discountStr = getValue(cols, 'discount %')
      const kickbackStr = getValue(cols, 'kickback %')
      const promoCode = getValue(cols, 'promo code')
      const t2Wallet = getValue(cols, 't2 wallet address')
      const t3Wallet = getValue(cols, 't3 wallet address')
      const t2Share = getValue(cols, 't2 share %')
      const t3Share = getValue(cols, 't3 share %')
      const name = getValue(cols, 'name')
      const telegramContact = getValue(cols, 'telegram contact')

      const contactKey = telegramContact.trim().toLowerCase()
      if (contactKey) {
        if (seenContacts.has(contactKey)) {
          duplicates++
          continue
        }
        seenContacts.add(contactKey)
      }

      const hasWallet = Boolean(t2Wallet || t3Wallet)
      if (!promoCode || !discountStr || !kickbackStr || !hasWallet) {
        invalid++
        continue
      }

      if (t2Wallet && !isValidWalletAddress(t2Wallet)) {
        invalid++
        continue
      }
      if (t3Wallet && !isValidWalletAddress(t3Wallet)) {
        invalid++
        continue
      }

      entries.push({
        promoTier,
        t2WalletAddress: t2Wallet,
        t3WalletAddress: t3Wallet,
        discountPercentage: parseNumber(discountStr),
        kickbackPercentage: parseNumber(kickbackStr),
        promoCode,
        t2SharePercentage: parseNumber(t2Share),
        t3SharePercentage: parseNumber(t3Share),
        contactName: name,
        contactTelegram: telegramContact,
      })
    }

    return { entries, invalid, duplicates }
  } catch {
    return {
      entries: [],
      invalid: 0,
      duplicates: 0,
      error: 'Failed to read CSV',
    }
  }
}
